"use client";

import { JobPosting } from "@/types/careers";
import { MapPin, Clock, Briefcase, CheckCircle } from "lucide-react";

interface JobDetailProps {
  job: JobPosting;
}

export default function JobDetail({ job }: JobDetailProps) {
  const { metadata, description } = job;

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#001640] to-[#284D93] p-6 md:p-8 text-white">
        <span className="inline-block px-3 py-1 text-xs font-semibold bg-white/20 rounded-full mb-4">
          {metadata.department}
        </span>
        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">
          {metadata.title}
        </h1>
        <div className="flex flex-wrap gap-4 text-sm text-white/90">
          <div className="flex items-center gap-2">
            <MapPin className="w-4 h-4" />
            <span>{metadata.location}</span>
          </div>
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            <span>{metadata.employmentType}</span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6 md:p-8 space-y-8">
        {/* Summary */}
        {description.summary && (
          <div>
            <h2 className="text-xl font-semibold text-[#284D93] mb-3">
              About the Role
            </h2>
            <p className="text-gray-700 leading-relaxed">{description.summary}</p>
          </div>
        )}

        {/* Responsibilities */}
        {description.responsibilities && description.responsibilities.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-[#284D93] mb-3">
              Responsibilities
            </h2>
            <ul className="space-y-2">
              {description.responsibilities.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#F6842B] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Requirements */}
        {description.requirements && description.requirements.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-[#284D93] mb-3">
              Requirements
            </h2>
            <ul className="space-y-2">
              {description.requirements.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#F6842B] mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Nice to Have */}
        {description.niceToHave && description.niceToHave.length > 0 && (
          <div>
            <h2 className="text-xl font-semibold text-[#284D93] mb-3">
              Nice to Have
            </h2>
            <ul className="space-y-2">
              {description.niceToHave.map((item, index) => (
                <li key={index} className="flex items-start gap-3">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                    <div className="w-2 h-2 bg-[#284D93] rounded-full" />
                  </div>
                  <span className="text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Benefits */}
        {description.benefits && description.benefits.length > 0 && (
          <div className="bg-gray-50 -mx-6 md:-mx-8 px-6 md:px-8 py-6 mt-8">
            <h2 className="text-xl font-semibold text-[#284D93] mb-3">
              What We Offer
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {description.benefits.map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-gray-700"
                >
                  <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Equal Opportunity Statement */}
        <div className="border-t border-gray-200 pt-6 mt-8">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">
            Equal Opportunity Employer
          </h3>
          <p className="text-sm text-gray-500 leading-relaxed">
            RevealSite is an equal opportunity employer. We celebrate diversity and are
            committed to creating an inclusive environment for all employees. All qualified
            applicants will receive consideration for employment without regard to race,
            color, religion, gender, gender identity or expression, sexual orientation,
            national origin, genetics, disability, age, or veteran status.
          </p>
        </div>
      </div>
    </div>
  );
}
